import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'

import { SiteLocale } from '../../../graphql/datoSchema.generated'
import stripUndefined from '../../../utils/stripUndefined'
import BlockZone, { normalizeTypename } from '../../../components/BlockZone'
import blocks from '../../../blocks'
import { blocksGetStaticProps } from '../../../blocks/index.server'
import notEmpty from '../../../utils/notEmpty'
import Layout from '../../../components/Layout'
import { getArticle } from '../../../graphql/article'
import { ArticleDetailFragment } from '../../../graphql/article.generated'
import { getArticleList } from '../../../graphql/articleList'
import { getSiteConfig, SiteConfig } from '../../../graphql/siteConfig'
import { getLayout, Layout as LayoutType } from '../../../graphql/layout'
import { getLocales } from '../../../utils/env'
import absolutify from '../../../utils/absolutify'
import Seo from '../../../components/Seo'
import resolveUrlForRecord from '../../../utils/resolveUrlForRecord'
import getAllPages from '../../../utils/getAllPages'
import MediaHeader from '../../../sections/MediaHeader'
import convertDatoImage from '../../../utils/convertDatoImage'
import { getSiteSettings, SiteSettings } from '../../../graphql/siteSettings'

const getArticleSlugs = async () => {
  return (
    (
      await Promise.all(
        getLocales().map(async (locale) => {
          const articleListPages = await getAllPages(
            (page) => getArticleList(locale, undefined, 100, page),
            100,
          )
          const articles = articleListPages.map((page) => page.articles).flat()
          return articles.map(({ slug }) => ({
            slug,
            locale,
          }))
        }),
      )
    )
      .flat()
      // Remove pages without valid slug (this should never happen since it's required in the CMS)
      .filter(
        (page): page is typeof page & { slug: string } =>
          page.slug !== undefined && page.slug !== null,
      )
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.FEATUREFLAG_STATICPATHS !== 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  return {
    paths: (await getArticleSlugs()).map(({ slug, locale }) => ({
      params: { slug },
      locale,
    })),
    fallback: 'blocking',
  }
}

export interface StaticProps {
  siteConfig: SiteConfig
  siteSettings: SiteSettings
  layout: LayoutType
  article: ArticleDetailFragment
  // [typename]: { [propName]: unknown }
  blockData: Record<string, Record<string, unknown>>
}

export const getStaticProps: GetStaticProps<
  StaticProps,
  { slug: string | string[] }
> = async ({ params, locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const slugPath = Array.isArray(params?.slug)
    ? params?.slug?.join('/').toLowerCase()
    : params?.slug?.toLowerCase()
  const slug = slugPath ?? ''
  if (!slug) {
    throw new Error('Missing required param: slug')
  }

  const [siteConfig, layout, article, _site] = await Promise.all([
    getSiteConfig(locale as SiteLocale),
    getLayout(locale as SiteLocale),
    getArticle(locale as SiteLocale, slug),
    getSiteSettings(locale as SiteLocale),
  ])

  if (!article) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const relatedArticlesCount = 3
  const relatedArticles = (
    await getArticleList(locale as SiteLocale, article.category?.id, undefined)
  ).articles
    .filter((related) => related.slug !== article.slug)
    .slice(0, relatedArticlesCount)

  const blockData = (
    await Promise.all(
      article.content.map(async (block) => {
        const typename = normalizeTypename(block.__typename)
        const getStaticProps = blocksGetStaticProps[typename]
        if (!getStaticProps) {
          return undefined
        }

        return [
          typename,
          await getStaticProps({
            locale: locale as SiteLocale,
            block,
          }),
        ] as const
      }),
    )
  )
    .filter(notEmpty)
    .reduce(
      (obj, [typename, props]) => ({
        ...obj,
        [typename]: props,
      }),
      {},
    )

  return {
    props: stripUndefined({
      siteConfig,
      siteSettings: _site,
      layout,
      article,
      relatedArticles,
      blockData,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      path: `/${siteConfig.newsIndex!.slug}/${slug}`,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

const PageResolver = ({
  siteConfig,
  siteSettings,
  layout,
  article,
  blockData,
}: StaticProps) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }
  dayjs.locale(locale)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const canonicalPath = `/${siteConfig.newsIndex!.slug}/${article.slug}`
  const articleDate = dayjs(article.publishDate).format('D MMM[,] YYYY')

  const fallbackTitle = siteSettings.globalSeo?.fallbackSeo?.title || undefined
  const fallbackDescription =
    siteSettings.globalSeo?.fallbackSeo?.description || undefined
  const fallbackTwitterCard =
    siteSettings.globalSeo?.fallbackSeo?.twitterCard || undefined
  const fallbackSharingImage = siteSettings?.globalSeo?.fallbackSeo?.image
    ? {
        src: siteSettings.globalSeo?.fallbackSeo?.image.url,
        alt: siteSettings.globalSeo?.fallbackSeo?.image.alt || '',
      }
    : undefined
  const titleSuffix = siteSettings?.globalSeo?.titleSuffix

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'news',
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          href: `/${siteConfig.newsIndex!.slug}/`,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          title: siteConfig.newsIndex!.seo?.title || 'News',
        },
        {
          key: 'article',
          href: canonicalPath,
          title: article.title || '?',
        },
      ]}
    >
      <Seo
        title={`${article.seo?.title || fallbackTitle} ${titleSuffix}`}
        description={article.seo?.description || fallbackDescription}
        image={convertDatoImage(article.seo?.image) || fallbackSharingImage}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        twitterCard={article.seo!.twitterCard! || fallbackTwitterCard}
        canonical={absolutify(canonicalPath, locale as SiteLocale)}
        paths={(article.allSlugs || []).reduce((obj, { locale, value }) => {
          if (locale && value) {
            // eslint-disable-next-line no-param-reassign
            obj[locale] = resolveUrlForRecord(
              siteConfig,
              {
                __typename: 'PageRecord',
                slug: value,
              },
              locale,
            )
          }
          return obj
        }, {} as Record<SiteLocale, string>)}
      />

      <MediaHeader
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        title={article.title!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        image={article.image!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        subline={[article.category!.title!, articleDate]}
      />
      <BlockZone
        components={blocks}
        content={article.content}
        data={blockData}
      />
    </Layout>
  )
}

export default PageResolver
